// src/utilities/tableFilters.ts
import { Row } from '@tanstack/react-table';
import {
  getDecimals,
  isNumericField,
} from '../features/recommendations/Table-widgets/TableData/RecommendationsTableData';

/* --- helper utilities --- */

const isDateColumn = (columnId: string) => {
  const id = (columnId ?? '').toString().toLowerCase();
  return (
    id.includes('date') || id.includes('created') || ['datestring', 'beforedatestring', 'afterdatestring'].includes(id)
  );
};

const formatDate = (dateStr: any) => {
  if (dateStr === null || dateStr === undefined || dateStr === '') return '';
  // If it's a Date already
  if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
    const d = dateStr;
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
  }
  const s = String(dateStr).trim();
  // if already MM/DD/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  // try Date parse
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
  }
  return s;
};

// Try to parse many common date formats to timestamp (ms). Returns NaN if cannot parse.
function parseToTimestamp(value: any): number {
  if (value === null || value === undefined || value === '') return NaN;

  // number (epoch seconds or ms)
  if (typeof value === 'number' && !isNaN(value)) {
    // Heuristic: if value looks like seconds (<= 10^10) convert to ms
    if (value < 1e11) return value * 1000;
    return value;
  }

  // Date instance
  if (value instanceof Date) {
    return value.getTime();
  }

  const s = String(value).trim();
  if (s === '') return NaN;

  // ISO-like or Date.parse friendly
  const isoTs = Date.parse(s);
  if (!isNaN(isoTs)) return isoTs;

  // Try MM/DD/YYYY or M/D/YYYY
  const mmddyyyy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;
  const m1 = s.match(mmddyyyy);
  if (m1) {
    const m = Number(m1[1]);
    const d = Number(m1[2]);
    const y = Number(m1[3]);
    // If month > 12, maybe user used DD/MM/YYYY — swap
    if (m > 12) {
      const tmp = m;
      // interpret as DD/MM/YYYY
      return new Date(y, d - 1, tmp).getTime();
    }
    return new Date(y, m - 1, d).getTime();
  }

  // Try DD MMM YYYY or DD-MMM-YYYY (e.g., 01 Jan 2023)
  const ddmmm = /^(\d{1,2})[ -]([A-Za-z]{3,})[ -]?(\d{4})$/;
  const m2 = s.match(ddmmm);
  if (m2) {
    const d = Number(m2[1]);
    const monthName = m2[2];
    const y = Number(m2[3]);
    const month = new Date(`${monthName} 1, ${y}`).getMonth();
    return new Date(y, month, d).getTime();
  }

  // Last resort: return NaN
  return NaN;
}

const formatNumberForFilter = (value: any, columnId: string, useFixedDecimals = false) => {
  if (value === null || value === undefined || isNaN(Number(value))) return '';
  return useFixedDecimals ? Number(value).toFixed(2) : Number(value).toFixed(getDecimals(columnId));
};

/* --- main factory --- */
export const createMultiConditionFilter = (
  setCurrentFilters: (filters: any[]) => void,
  currentFiltersRef: React.RefObject<any[]>,
  useFixedDecimals = false,
  rawData?: any,
) => {
  const normalizeOpLocal = (op: any) => {
    const raw = (op ?? '').toString().trim().toLowerCase();
    const map: Record<string, string> = {
      '>': 'is greater than',
      '<': 'is less than',
      '>=': 'is greater than or equal to',
      '<=': 'is less than or equal to',
      'on or after': 'is after or equal to',
      'on or before': 'is before or equal to',
      after: 'is after',
      before: 'is before',
    };
    return map[raw] ?? raw;
  };

  function applySingleCondition(cellValue: any, condition: any, columnId: string) {
    const op = normalizeOpLocal(condition?.operator);
    const rawFilterVal = condition?.value;

    if (isDateColumn(columnId)) {
      const leftTs = parseToTimestamp(cellValue);
      const rightTs = parseToTimestamp(rawFilterVal);
      const leftValid = !isNaN(leftTs);
      const rightValid = !isNaN(rightTs);

      if (
        [
          'is after or equal to',
          'is after',
          'is before or equal to',
          'is before',
          'is greater than',
          'is less than',
        ].includes(op)
      ) {
        if (!leftValid || !rightValid) return false;
        switch (op) {
          case 'is after or equal to':
            return leftTs >= rightTs;
          case 'is after':
          case 'is greater than':
            return leftTs > rightTs;
          case 'is before or equal to':
            return leftTs <= rightTs;
          case 'is before':
          case 'is less than':
            return leftTs < rightTs;
          default:
            return false;
        }
      }

      if (op === 'is null') return !leftValid;
      if (op === 'is not null') return leftValid;
      if (op === 'is empty') return !leftValid;
      if (op === 'is not empty') return leftValid;

      const leftStr = (formatDate(cellValue) ?? '').toString().toLowerCase();
      const rightStr = (formatDate(rawFilterVal) ?? '').toString().toLowerCase();

      switch (op) {
        case 'starts with':
          return leftStr.startsWith(rightStr);
        case 'contains':
          return leftStr.includes(rightStr);
        case 'does not contain':
          return !leftStr.includes(rightStr);
        case 'ends with':
          return leftStr.endsWith(rightStr);
        case 'is equal to':
          return leftStr === rightStr;
        case 'is not equal to':
          return leftStr !== rightStr;
        default:
          return false;
      }
    }

    if (isNumericField(columnId)) {
      const leftNum = Number(cellValue);
      const rightNum = Number(rawFilterVal);
      const leftNumValid = !isNaN(leftNum);
      const rightNumValid = !isNaN(rightNum);

      if (['is greater than', 'is less than', 'is greater than or equal to', 'is less than or equal to'].includes(op)) {
        if (!leftNumValid || !rightNumValid) return false;
        switch (op) {
          case 'is greater than':
            return leftNum > rightNum;
          case 'is less than':
            return leftNum < rightNum;
          case 'is greater than or equal to':
            return leftNum >= rightNum;
          case 'is less than or equal to':
            return leftNum <= rightNum;
          default:
            return false;
        }
      }

      const leftStr = formatNumberForFilter(cellValue, columnId, useFixedDecimals).toString().toLowerCase();
      const rightStr = (rawFilterVal == null ? '' : String(rawFilterVal)).toLowerCase();

      switch (op) {
        case 'contains':
          return leftStr.includes(rightStr);
        case 'does not contain':
          return !leftStr.includes(rightStr);
        case 'starts with':
          return leftStr.startsWith(rightStr);
        case 'ends with':
          return leftStr.endsWith(rightStr);
        case 'is equal to':
          return leftStr === rightStr;
        case 'is not equal to':
          return leftStr !== rightStr;
        default:
          return false;
      }
    }

    const left = (cellValue == null ? '' : String(cellValue)).toLowerCase();
    const right = (rawFilterVal == null ? '' : String(rawFilterVal)).toLowerCase();

    switch (op) {
      case 'is equal to':
        return left === right;
      case 'is not equal to':
        return left !== right;
      case 'starts with':
        return left.startsWith(right);
      case 'contains':
        return left.includes(right);
      case 'does not contain':
        return !left.includes(right);
      case 'ends with':
        return left.endsWith(right);
      case 'is empty':
        return left === '';
      case 'is not empty':
        return left !== '';
      case 'is null':
        return !left;
      case 'is not null':
        return !!left;
      default:
        return false;
    }
  }

  const safeUpdateCurrentFilters = (columnId: string, payload: any) => {
    try {
      const existing = currentFiltersRef.current
        ? currentFiltersRef.current.filter((f) => f.columnId !== columnId)
        : [];
      existing.push({ columnId, filterValue: payload });

      const cur = currentFiltersRef.current;
      if (cur) {
        cur.length = 0;
        for (const f of existing) cur.push(f);
        setCurrentFilters([...cur]);
      } else {
        setCurrentFilters([...existing]);
      }
    } catch (e) {
      // keep linter happy — intentionally no-op
      void e;
    }
  };

  return function multiConditionFilter<TData = any>(row: Row<TData>, columnId: string, filterValue: any) {
    const payload =
      filterValue ?? currentFiltersRef?.current?.find((f) => f.columnId === columnId)?.filterValue ?? null;
    if (!payload) return true;

    const logicRaw = payload.logicOperator ?? payload.logicBetween ?? payload.logic ?? 'And';
    const logic = String(logicRaw).trim().toLowerCase() === 'or' ? 'or' : 'and';
    const conditions = Array.isArray(payload.conditions) ? payload.conditions : [];
    if (conditions.length === 0) {
      safeUpdateCurrentFilters(columnId, payload);
      return true;
    }

    let cellValue: any = undefined;
    const rAny: any = row;

    if (rAny?.original && rAny.original[columnId] !== undefined) {
      cellValue = rAny.original[columnId];
    }

    if ((cellValue === undefined || cellValue === null) && typeof rAny.getValue === 'function') {
      try {
        const gv = rAny.getValue(columnId);
        if (gv !== undefined && gv !== null) cellValue = gv;
      } catch (e) {
        void e;
      }
    }

    if (
      (cellValue === undefined || cellValue === null) &&
      rAny?.original &&
      typeof rAny.original[columnId] === 'object'
    ) {
      const v = rAny.original[columnId];
      if (v && typeof v === 'object') {
        if ('value' in v) cellValue = v.value;
        else if ('Value' in v) cellValue = v.Value;
      }
    }

    if ((cellValue === undefined || cellValue === null) && rawData && Array.isArray(rawData)) {
      const wellCandidates: any[] = [];
      if (rAny?.original?.Well !== undefined) wellCandidates.push(rAny.original.Well);
      if (rAny?.original?.well !== undefined) wellCandidates.push(rAny.original.well);
      if (rAny?.original?.Well?.value !== undefined) wellCandidates.push(rAny.original.Well.value);
      if (rAny?.original?.well?.value !== undefined) wellCandidates.push(rAny.original.well.value);
      if (typeof rAny.getValue === 'function') {
        try {
          const w = rAny.getValue('Well');
          if (w !== undefined && w !== null) wellCandidates.push(w);
        } catch (e) {
          void e;
        }
        try {
          const w2 = rAny.getValue('well');
          if (w2 !== undefined && w2 !== null) wellCandidates.push(w2);
        } catch (e) {
          void e;
        }
      }

      const wellKey = wellCandidates.length ? String(wellCandidates[0]) : undefined;
      if (wellKey) {
        let found: any = undefined;
        for (const rawItem of rawData) {
          const k =
            rawItem?.Well?.value ??
            rawItem?.Well ??
            rawItem?.well?.value ??
            rawItem?.well ??
            rawItem?.wellId ??
            rawItem?.WellId;
          if (k !== undefined && String(k) === wellKey) {
            found = rawItem;
            break;
          }
        }
        if (found) {
          const maybe = found[columnId];
          if (maybe !== undefined && maybe !== null) {
            cellValue =
              maybe && typeof maybe === 'object' && ('value' in maybe || 'Value' in maybe)
                ? (maybe.value ?? maybe.Value)
                : maybe;
          }
        }
      }
    }

    if (cellValue && typeof cellValue === 'object' && 'value' in cellValue) {
      // access by bracket to avoid unnecessary assertion
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      cellValue = (cellValue as unknown as Record<string, unknown>)['value'];
    }

    if (cellValue === undefined) {
      // eslint-disable-next-line no-console
      console.debug('[filter] cellValue undefined for', { columnId, rowOriginal: rAny?.original, payload });
    }

    const results: boolean[] = (conditions || []).map((cond: any) => {
      try {
        return !!applySingleCondition(cellValue, cond, columnId);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.debug('[filter] applySingleCondition error', { columnId, cond, cellValue, err });
        return false;
      }
    });

    if (!Array.isArray(results) || results.length === 0) {
      safeUpdateCurrentFilters(columnId, payload);
      return true;
    }

    const keep = logic === 'or' ? results.some(Boolean) : results.every(Boolean);
    safeUpdateCurrentFilters(columnId, payload);
    return !!keep;
  };
};
